import { Box, Dimensions, PackagingSolution, Product, ProductPlacement, Position, BoxInstance } from '../types/index.ts';
import { MultiProductRequest } from '../types/index.ts';

type Orientation = 'standard' | 'tilted' | 'length-rotated' | 'width-rotated' | 'flipped' | 'combined-rotation';

// Clearance subtracted from each box dimension, in inches (all computations
// are normalized to inches). Set per call by the public functions from their
// `marginInches` argument; 0 means a product exactly the size of the box fits.
let SAFETY_MARGIN = 0;
// Epsilon to absorb floating point noise, notably so an exact division
// (e.g. 12 / 12, or values from cm→inch conversion) floors correctly.
const EPS = 1e-6;
// Caps so huge quantities cannot freeze the browser: numbers stay exact,
// only the count of generated 3D placements/instances is bounded.
const MAX_PLACEMENTS_PER_BOX = 500;
const MAX_INSTANCES = 50;

const round3 = (value: number): number => parseFloat(value.toFixed(3));

/**
 * Convert dimensions between units. The two factors are exact inverses so a
 * cm → in → cm round-trip is faithful.
 */
export const convertDimensions = (dimensions: Dimensions, fromUnit: string, toUnit = 'in'): Dimensions => {
  if (fromUnit === toUnit) return dimensions;
  const factor = fromUnit === 'cm' && toUnit === 'in' ? 1 / 2.54 : 2.54;
  return {
    length: round3(dimensions.length * factor),
    width: round3(dimensions.width * factor),
    height: round3(dimensions.height * factor),
  };
};

const isValidDimensions = (d: Dimensions | undefined): boolean =>
  !!d &&
  Number.isFinite(d.length) && d.length > 0 &&
  Number.isFinite(d.width) && d.width > 0 &&
  Number.isFinite(d.height) && d.height > 0;

const isValidProduct = (p: Product): boolean =>
  isValidDimensions(p.dimensions) && Number.isFinite(p.quantity) && p.quantity >= 1;

const isValidBox = (b: Box): boolean => isValidDimensions(b.dimensions);

const normalizeToInches = (dimensions: Dimensions, unit?: string): Dimensions =>
  unit === 'cm' ? convertDimensions(dimensions, 'cm', 'in') : dimensions;

/**
 * Candidate (length, width, height) permutations for a given orientation.
 * 'combined-rotation' has three candidates (the two crossed permutations plus
 * standard); the others have exactly one.
 */
const orientationCandidates = (d: Dimensions, orientation: Orientation): Dimensions[] => {
  switch (orientation) {
    case 'standard':
    case 'tilted':
      return [{ length: d.length, width: d.width, height: d.height }];
    case 'length-rotated':
      return [{ length: d.length, width: d.height, height: d.width }];
    case 'width-rotated':
      return [{ length: d.width, width: d.height, height: d.length }];
    case 'flipped':
      return [{ length: d.height, width: d.width, height: d.length }];
    case 'combined-rotation':
      return [
        { length: d.height, width: d.length, height: d.width },
        { length: d.width, width: d.length, height: d.height },
        { length: d.length, width: d.width, height: d.height },
      ];
    default:
      return [{ length: d.length, width: d.width, height: d.height }];
  }
};

/** How many products fit along each axis for the given oriented dimensions. */
const axisCounts = (oriented: Dimensions, box: Dimensions) => {
  const nLength = Math.max(0, Math.floor((box.length - SAFETY_MARGIN) / oriented.length + EPS));
  const nWidth = Math.max(0, Math.floor((box.width - SAFETY_MARGIN) / oriented.width + EPS));
  const nHeight = Math.max(0, Math.floor((box.height - SAFETY_MARGIN) / oriented.height + EPS));
  return { nLength, nWidth, nHeight, total: nLength * nWidth * nHeight };
};

/**
 * Best oriented dimensions for a product in a box under a given orientation
 * (for 'combined-rotation' the candidate holding the most products wins).
 */
const bestOriented = (
  productDimensions: Dimensions,
  boxDimensions: Dimensions,
  orientation: Orientation
): { dims: Dimensions; counts: ReturnType<typeof axisCounts> } => {
  let best: { dims: Dimensions; counts: ReturnType<typeof axisCounts> } | null = null;
  for (const dims of orientationCandidates(productDimensions, orientation)) {
    const counts = axisCounts(dims, boxDimensions);
    if (!best || counts.total > best.counts.total) {
      best = { dims, counts };
    }
  }
  return best!;
};

/** How many products fit in a box for a given orientation. Pure grid geometry. */
const calculateProductsPerBox = (
  productDimensions: Dimensions,
  boxDimensions: Dimensions,
  orientation: Orientation
): number => bestOriented(productDimensions, boxDimensions, orientation).counts.total;

/** Space utilization (percentage) for a single product type. */
const calculateSpaceUtilization = (
  productDimensions: Dimensions,
  boxDimensions: Dimensions,
  productsPerBox: number
): number => {
  const boxVolume = boxDimensions.length * boxDimensions.width * boxDimensions.height;
  if (boxVolume <= 0) return 0;
  const productVolume = productDimensions.length * productDimensions.width * productDimensions.height;
  return (productVolume * productsPerBox / boxVolume) * 100;
};

const volumeOf = (d: Dimensions): number => d.length * d.width * d.height;

/**
 * Grid placements for a single product type. Products are packed corner-first
 * with a pitch equal to their oriented dimensions, so the number of placements
 * always matches the advertised capacity.
 */
const calculateProductPlacements = (
  product: Product,
  boxDimensions: Dimensions,
  orientation: Orientation,
  productsPerBox: number
): ProductPlacement[] => {
  const productDimensions = normalizeToInches(product.dimensions, product.unit);
  const { dims, counts } = bestOriented(productDimensions, boxDimensions, orientation);

  // Inset every placement by the per-side margin so the clearance is honoured on
  // all six faces (counts already reserve 2× the margin per axis; SAFETY_MARGIN
  // is that 2× per-axis total, so the per-side margin is half of it).
  const margin = SAFETY_MARGIN / 2;
  const startX = -boxDimensions.width / 2 + margin;
  const startY = -boxDimensions.height / 2 + margin;
  const startZ = -boxDimensions.length / 2 + margin;

  const placements: ProductPlacement[] = [];
  const target = Math.min(productsPerBox, counts.total, MAX_PLACEMENTS_PER_BOX);

  let count = 0;
  for (let z = 0; z < counts.nLength && count < target; z++) {
    for (let y = 0; y < counts.nHeight && count < target; y++) {
      for (let x = 0; x < counts.nWidth && count < target; x++) {
        placements.push({
          productIndex: 0,
          dimensions: dims,
          position: {
            x: startX + dims.width / 2 + dims.width * x,
            y: startY + dims.height / 2 + dims.height * y,
            z: startZ + dims.length / 2 + dims.length * z,
          },
        });
        count++;
      }
    }
  }
  return placements;
};

type OccupiedSpace = {
  x: number; y: number; z: number;
  width: number; height: number; length: number;
  productIndex: number;
  productId: string;
};

/**
 * Greedy placement of multiple product types in one box: largest first,
 * stacking identical products, then smaller on larger, then floor grid.
 * Each product is placed in its own best orientation (`orientedById`), and the
 * generated array is capped at MAX_PLACEMENTS_PER_BOX like the single-product
 * path (callers account for counts separately).
 */
const calculateMultiProductPlacements = (
  products: Product[],
  boxDimensions: Dimensions,
  orientedById: { [productId: string]: Dimensions },
  productsPerBox: { [productId: string]: number }
): ProductPlacement[] => {
  const allPlacements: ProductPlacement[] = [];

  // Per-side margin: inset the usable region by `margin` on every face so the
  // clearance is symmetric on all six sides (SAFETY_MARGIN is the 2× per-axis
  // total, so start and end each move in by half of it).
  const margin = SAFETY_MARGIN / 2;
  const startX = -boxDimensions.width / 2 + margin;
  const startY = -boxDimensions.height / 2 + margin;
  const startZ = -boxDimensions.length / 2 + margin;
  const endX = boxDimensions.width / 2 - margin;
  const endY = boxDimensions.height / 2 - margin;
  const endZ = boxDimensions.length / 2 - margin;

  const occupiedSpaces: OccupiedSpace[] = [];

  const adjustedProducts = products.map((product, index) => {
    const dims = orientedById[product.id] ?? normalizeToInches(product.dimensions, product.unit);
    return {
      index,
      id: product.id,
      dimensions: dims,
      count: productsPerBox[product.id] || 0,
      volume: volumeOf(dims),
    };
  });

  const sortedProducts = adjustedProducts.filter((p) => p.count > 0).sort((a, b) => b.volume - a.volume);

  const isFree = (x: number, y: number, z: number, w: number, h: number, l: number): boolean => {
    if (x < startX - EPS || x + w > endX + EPS ||
        y < startY - EPS || y + h > endY + EPS ||
        z < startZ - EPS || z + l > endZ + EPS) {
      return false;
    }
    for (const s of occupiedSpaces) {
      if (!(x + w <= s.x + EPS || x >= s.x + s.width - EPS ||
            y + h <= s.y + EPS || y >= s.y + s.height - EPS ||
            z + l <= s.z + EPS || z >= s.z + s.length - EPS)) {
        return false;
      }
    }
    return true;
  };

  const findValidPlacement = (w: number, h: number, l: number, productId: string): Position | null => {
    // 1. Stack on an identical product
    for (const s of occupiedSpaces) {
      if (s.productId === productId && Math.abs(w - s.width) < 0.1 && Math.abs(l - s.length) < 0.1) {
        const y = s.y + s.height;
        if (isFree(s.x, y, s.z, w, h, l)) return { x: s.x + w / 2, y: y + h / 2, z: s.z + l / 2 };
      }
    }
    // 2. Stack a smaller product on a larger one (centered, then corners)
    for (const s of occupiedSpaces) {
      if (w <= s.width + EPS && l <= s.length + EPS) {
        const y = s.y + s.height;
        const candidates = [
          { x: s.x + (s.width - w) / 2, z: s.z + (s.length - l) / 2 },
          { x: s.x, z: s.z },
          { x: s.x + s.width - w, z: s.z },
          { x: s.x, z: s.z + s.length - l },
          { x: s.x + s.width - w, z: s.z + s.length - l },
        ];
        for (const c of candidates) {
          if (isFree(c.x, y, c.z, w, h, l)) return { x: c.x + w / 2, y: y + h / 2, z: c.z + l / 2 };
        }
      }
    }
    // 3. Floor grid, coarse pitch (own dimensions)
    for (let z = startZ; z + l <= endZ + EPS; z += l) {
      for (let x = startX; x + w <= endX + EPS; x += w) {
        if (isFree(x, startY, z, w, h, l)) return { x: x + w / 2, y: startY + h / 2, z: z + l / 2 };
      }
    }
    // 4. Fine scan of the whole volume, bounded resolution
    const stepX = Math.max(w / 2, boxDimensions.width / 40);
    const stepY = Math.max(h / 2, boxDimensions.height / 40);
    const stepZ = Math.max(l / 2, boxDimensions.length / 40);
    for (let y = startY; y + h <= endY + EPS; y += stepY) {
      for (let z = startZ; z + l <= endZ + EPS; z += stepZ) {
        for (let x = startX; x + w <= endX + EPS; x += stepX) {
          if (isFree(x, y, z, w, h, l)) return { x: x + w / 2, y: y + h / 2, z: z + l / 2 };
        }
      }
    }
    return null;
  };

  for (const product of sortedProducts) {
    for (let i = 0; i < product.count; i++) {
      if (allPlacements.length >= MAX_PLACEMENTS_PER_BOX) return allPlacements;
      const { width, height, length } = product.dimensions;
      const position = findValidPlacement(width, height, length, product.id);
      if (!position) break;
      allPlacements.push({ productIndex: product.index, dimensions: product.dimensions, position });
      occupiedSpaces.push({
        x: position.x - width / 2,
        y: position.y - height / 2,
        z: position.z - length / 2,
        width, height, length,
        productIndex: product.index,
        productId: product.id,
      });
    }
  }
  return allPlacements;
};

/** Placements for every box instance of a single-product solution. */
const calculateAllBoxInstances = (
  product: Product,
  boxDimensions: Dimensions,
  orientation: Orientation,
  productsPerBox: number,
  totalBoxesNeeded: number
): BoxInstance[] => {
  const boxInstances: BoxInstance[] = [];
  let remaining = product.quantity;
  const instancesToGenerate = Math.min(totalBoxesNeeded, MAX_INSTANCES);
  for (let i = 0; i < instancesToGenerate && remaining > 0; i++) {
    const productsInThisBox = Math.min(productsPerBox, remaining);
    boxInstances.push({
      instanceIndex: i,
      productPlacements: calculateProductPlacements(
        { ...product, quantity: productsInThisBox },
        boxDimensions,
        orientation,
        productsInThisBox
      ),
    });
    remaining -= productsInThisBox;
  }
  return boxInstances;
};

type MultiPackingResult = {
  totalBoxes: number;
  boxInstances: BoxInstance[];
  firstBoxCounts: { [productId: string]: number };
  firstBoxTotal: number;
};

/**
 * Simulates the greedy packing box by box until every product is exhausted.
 * Each box is planned largest-product-first up to each product's solo grid
 * capacity, then verified geometrically (per-product orientation). The number
 * of boxes reported is exactly the number of boxes the simulation fills —
 * never an aggregate division, which drifts as products run out at different
 * rates. Consecutive identical boxes are counted in bulk so huge orders stay
 * cheap, and only the first MAX_INSTANCES boxes carry 3D placements.
 */
const simulateMultiProductPacking = (
  products: Product[],
  boxDimensions: Dimensions,
  orientedById: { [productId: string]: Dimensions },
  capacities: { [productId: string]: number }
): MultiPackingResult => {
  const remaining: { [productId: string]: number } = {};
  products.forEach((product) => {
    remaining[product.id] = product.quantity;
  });

  const order = products
    .map((product, index) => ({ product, index, volume: volumeOf(orientedById[product.id]) }))
    .sort((a, b) => b.volume - a.volume);

  const boxInstances: BoxInstance[] = [];
  let totalBoxes = 0;
  let firstBoxCounts: { [productId: string]: number } = {};
  let firstBoxTotal = 0;

  // Each phase either exhausts a product or stops, so the loop is short in
  // practice; the bound is a safety net, with a throughput fallback below.
  for (let phase = 0; phase < 10000; phase++) {
    const remainingTotal = Object.values(remaining).reduce((sum, count) => sum + count, 0);
    if (remainingTotal === 0) break;

    // Plan this box: largest product first, up to its solo capacity.
    const planned: { [productId: string]: number } = {};
    let plannedTotal = 0;
    for (const { product } of order) {
      const count = Math.min(remaining[product.id], capacities[product.id] || 0);
      if (count > 0) {
        planned[product.id] = count;
        plannedTotal += count;
      }
    }
    if (plannedTotal === 0) break;

    let actual: { [productId: string]: number } = {};
    let placements: ProductPlacement[];

    if (plannedTotal <= MAX_PLACEMENTS_PER_BOX) {
      // Verify the plan geometrically: what really fits together in one box.
      placements = calculateMultiProductPlacements(products, boxDimensions, orientedById, planned);
      placements.forEach((p) => {
        const id = products[p.productIndex].id;
        actual[id] = (actual[id] || 0) + 1;
      });
    } else {
      // Verifying thousands of placements one by one is what used to freeze
      // the browser. Above the cap, fill by capacity fractions instead —
      // conservative, and exact whenever one product dominates the box.
      let budget = 1;
      for (const { product } of order) {
        const capacity = capacities[product.id] || 0;
        if (capacity <= 0 || budget <= EPS) continue;
        const count = Math.min(planned[product.id] || 0, Math.floor(budget * capacity + EPS));
        if (count > 0) {
          actual[product.id] = count;
          budget -= count / capacity;
        }
      }
      // 3D display stays capped; the counts above remain the real ones.
      const display: { [productId: string]: number } = {};
      let displayTotal = 0;
      for (const { product } of order) {
        if (displayTotal >= MAX_PLACEMENTS_PER_BOX) break;
        const count = Math.min(actual[product.id] || 0, MAX_PLACEMENTS_PER_BOX - displayTotal);
        if (count > 0) {
          display[product.id] = count;
          displayTotal += count;
        }
      }
      placements = calculateMultiProductPlacements(products, boxDimensions, orientedById, display);
    }

    const actualTotal = Object.values(actual).reduce((sum, count) => sum + count, 0);
    if (actualTotal === 0) break; // nothing placeable even in an empty box

    // Bulk-count consecutive identical boxes: the layout repeats while every
    // product's plan (min(remaining, capacity)) stays unchanged.
    let maxRepeats = Infinity;
    for (const { product } of order) {
      const id = product.id;
      const placed = actual[id] || 0;
      if (placed <= 0) continue;
      maxRepeats = Math.min(maxRepeats, Math.floor(remaining[id] / placed));
      if ((planned[id] || 0) >= (capacities[id] || 0) && remaining[id] > (capacities[id] || 0)) {
        // Capacity-limited: the plan stays `capacity` while remaining >= capacity.
        maxRepeats = Math.min(maxRepeats, Math.floor((remaining[id] - (capacities[id] || 0)) / placed) + 1);
      } else {
        // Quantity-limited: the next box's plan shrinks, so no bulk repeat.
        maxRepeats = Math.min(maxRepeats, 1);
      }
    }
    const repeats = Math.max(1, Number.isFinite(maxRepeats) ? maxRepeats : 1);

    if (totalBoxes === 0) {
      firstBoxCounts = { ...actual };
      firstBoxTotal = actualTotal;
    }
    for (let i = 0; i < repeats && boxInstances.length < MAX_INSTANCES; i++) {
      boxInstances.push({ instanceIndex: boxInstances.length, productPlacements: placements });
    }
    totalBoxes += repeats;
    Object.keys(actual).forEach((id) => {
      remaining[id] -= actual[id] * repeats;
    });
  }

  // Safety-net accounting: if the phase bound tripped with stock left, extend
  // the count with the first box's throughput rather than understating it.
  const leftover = Object.values(remaining).reduce((sum, count) => sum + count, 0);
  if (leftover > 0 && firstBoxTotal > 0) {
    totalBoxes += Math.ceil(leftover / firstBoxTotal);
  }

  return { totalBoxes, boxInstances, firstBoxCounts, firstBoxTotal };
};

/** Orientations to evaluate for a product, from its selected rotation options. */
const orientationsForProduct = (product: Product): Orientation[] => {
  const orientations: Orientation[] = ['standard'];
  const hasLength = product.rotationOptions.includes('length');
  const hasWidth = product.rotationOptions.includes('width');
  const hasFlipped = product.rotationOptions.includes('flipped');
  if (hasLength) orientations.push('length-rotated');
  if (hasWidth) orientations.push('width-rotated');
  if (hasFlipped) orientations.push('flipped');
  if (hasLength && hasWidth) orientations.push('combined-rotation');
  return orientations;
};

// Priority used to break ties between orientations with equal capacity.
const ORIENTATION_PRIORITY: { [key: string]: number } = {
  standard: 0,
  'length-rotated': 1,
  'width-rotated': 2,
  flipped: 3,
  'combined-rotation': 4,
  tilted: 5,
};

/**
 * Finds the best packaging solutions for a single product. Every selected
 * orientation is evaluated; for each box the orientation holding the most
 * products wins (standard wins ties).
 */
export const findBestPackagingSolutions = (
  product: Product,
  availableBoxes: Box[],
  marginInches: number = 0
): PackagingSolution[] => {
  // marginInches is the clearance PER SIDE, so subtract it twice per axis.
  SAFETY_MARGIN = Number.isFinite(marginInches) && marginInches > 0 ? 2 * marginInches : 0;
  if (!isValidProduct(product)) return [];

  const visibleBoxes = availableBoxes.filter((box) => box.visible !== false && isValidBox(box));
  const productDimensions = normalizeToInches(product.dimensions, product.unit);
  const possibleOrientations = orientationsForProduct(product);

  const solutions: PackagingSolution[] = [];

  visibleBoxes.forEach((box) => {
    const boxDimensions = normalizeToInches(box.dimensions, box.unit);

    let bestOrientation: Orientation | null = null;
    let bestProductsPerBox = 0;

    for (const orientation of possibleOrientations) {
      const productsPerBox = calculateProductsPerBox(productDimensions, boxDimensions, orientation);
      if (productsPerBox <= 0) continue;
      if (
        bestOrientation === null ||
        productsPerBox > bestProductsPerBox ||
        (productsPerBox === bestProductsPerBox &&
          ORIENTATION_PRIORITY[orientation] < ORIENTATION_PRIORITY[bestOrientation])
      ) {
        bestOrientation = orientation;
        bestProductsPerBox = productsPerBox;
      }
    }

    if (bestOrientation === null) return;

    const totalBoxesNeeded = Math.ceil(product.quantity / bestProductsPerBox);
    const spaceUtilization = calculateSpaceUtilization(
      bestOriented(productDimensions, boxDimensions, bestOrientation).dims,
      boxDimensions,
      bestProductsPerBox
    );

    solutions.push({
      boxId: box.id,
      boxName: box.name,
      boxDimensions: box.dimensions,
      boxUnit: box.unit || 'in',
      productsPerBox: bestProductsPerBox,
      totalBoxesNeeded,
      orientation: bestOrientation,
      spaceUtilization,
      productPlacements: calculateProductPlacements(product, boxDimensions, bestOrientation, bestProductsPerBox),
      boxInstances: calculateAllBoxInstances(product, boxDimensions, bestOrientation, bestProductsPerBox, totalBoxesNeeded),
    });
  });

  return solutions.sort((a, b) => {
    if (a.totalBoxesNeeded !== b.totalBoxesNeeded) return a.totalBoxesNeeded - b.totalBoxesNeeded;
    return b.spaceUtilization - a.spaceUtilization;
  });
};

/** Finds the best packaging solutions for multiple products. */
export const findMultiProductPackagingSolutions = (
  multiProductRequest: MultiProductRequest,
  availableBoxes: Box[],
  marginInches: number = 0
): PackagingSolution[] => {
  // marginInches is the clearance PER SIDE, so subtract it twice per axis.
  SAFETY_MARGIN = Number.isFinite(marginInches) && marginInches > 0 ? 2 * marginInches : 0;
  const products = multiProductRequest.products;

  if (products.length === 1) {
    return findBestPackagingSolutions(products[0], availableBoxes, marginInches);
  }
  if (products.some((p) => !isValidProduct(p))) return [];

  const visibleBoxes = availableBoxes.filter((box) => box.visible !== false && isValidBox(box));

  const normalizedProducts = products.map((product) => ({
    ...product,
    normalizedDimensions: normalizeToInches(product.dimensions, product.unit),
  }));

  const totalProductsQuantity = normalizedProducts.reduce((sum, product) => sum + product.quantity, 0);
  const solutions: PackagingSolution[] = [];

  visibleBoxes.forEach((box) => {
    const boxDimensions = normalizeToInches(box.dimensions, box.unit);

    // Per-product best orientation for THIS box: its oriented dimensions are
    // what the placement engine uses, so a product that only fits rotated is
    // really packed rotated instead of being silently dropped.
    const capacities: { [productId: string]: number } = {};
    const orientedById: { [productId: string]: Dimensions } = {};
    const bestOrientations: { [productId: string]: Orientation } = {};

    for (const product of normalizedProducts) {
      let bestCount = 0;
      let bestOrientationForProduct: Orientation | null = null;
      let bestDims: Dimensions | null = null;
      for (const orientation of orientationsForProduct(product)) {
        const { dims, counts } = bestOriented(product.normalizedDimensions, boxDimensions, orientation);
        if (counts.total > bestCount) {
          bestCount = counts.total;
          bestOrientationForProduct = orientation;
          bestDims = dims;
        }
      }
      if (bestCount > 0 && bestOrientationForProduct && bestDims) {
        capacities[product.id] = bestCount;
        orientedById[product.id] = bestDims;
        bestOrientations[product.id] = bestOrientationForProduct;
      }
    }

    // Every product must fit in this box in at least one orientation.
    if (Object.keys(capacities).length !== normalizedProducts.length) return;

    const packing = simulateMultiProductPacking(normalizedProducts, boxDimensions, orientedById, capacities);
    if (packing.firstBoxTotal <= 0 || packing.boxInstances.length === 0) return; // box not actually usable

    const boxVolume = volumeOf(boxDimensions);
    let placedVolume = 0;
    normalizedProducts.forEach((product) => {
      placedVolume += volumeOf(orientedById[product.id]) * (packing.firstBoxCounts[product.id] || 0);
    });
    const spaceUtilization = boxVolume > 0 ? (placedVolume / boxVolume) * 100 : 0;

    const orientationValues = Object.values(bestOrientations);
    const globalOrientation: Orientation =
      orientationValues.length > 0 && orientationValues.every((o) => o === orientationValues[0])
        ? orientationValues[0]
        : 'standard';

    solutions.push({
      boxId: box.id,
      boxName: box.name,
      boxDimensions: box.dimensions,
      boxUnit: box.unit || 'in',
      productsPerBox: packing.firstBoxTotal,
      totalBoxesNeeded: packing.totalBoxes,
      orientation: globalOrientation,
      spaceUtilization,
      products: multiProductRequest.products,
      totalProductsQuantity,
      productPlacements: packing.boxInstances[0]?.productPlacements ?? [],
      boxInstances: packing.boxInstances,
    });
  });

  return solutions.sort((a, b) => {
    if (a.totalBoxesNeeded !== b.totalBoxesNeeded) return a.totalBoxesNeeded - b.totalBoxesNeeded;
    return b.spaceUtilization - a.spaceUtilization;
  });
};
